import { X } from "@phosphor-icons/react";
import OrgBadge from "@/components/OrgBadge";
import StatusBadge from "@/components/StatusBadge";
import { TEST_IDS } from "@/constants/testIds";

const nf = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 5 });

const ROWS = [
    { key: "value", label: "Value", fmt: (v, unit) => `${nf.format(v)} ${unit || ""}` },
    { key: "unit", label: "Unit" },
    { key: "functional_unit", label: "Functional Unit" },
    { key: "methodology", label: "Methodology" },
    { key: "system_boundary", label: "System Boundary" },
    { key: "allocation_method", label: "Allocation" },
    { key: "geography", label: "Geography" },
    { key: "year", label: "Year" },
    { key: "confidence", label: "Confidence", fmt: (v) => `${v}%` },
    { key: "data_quality", label: "Data Quality" },
    { key: "community_rating", label: "Rating", fmt: (v) => `${v.toFixed(1)} ★` },
    { key: "agree_count", label: "Agree" },
    { key: "disagree_count", label: "Disagree" },
    { key: "peer_reviewed", label: "Peer Reviewed", fmt: (v) => (v ? "Yes" : "No") },
    { key: "version", label: "Version", fmt: (v) => `v${v}` },
];

/**
 * Full-screen comparison modal presenting selected community datasets
 * side-by-side across every methodology attribute.
 */
export default function ComparisonModal({ datasets, onClose, unit }) {
    return (
        <div
            data-testid={TEST_IDS.comparisonModal}
            className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 md:p-10 overflow-auto"
            onClick={onClose}
        >
            <div
                className="bg-white border border-black shadow-brut w-full max-w-6xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
                    <div>
                        <div className="label-caps">Compare submissions</div>
                        <h2 className="font-serif text-2xl mt-1">
                            {datasets.length} datasets side-by-side
                        </h2>
                    </div>
                    <button
                        data-testid={TEST_IDS.comparisonClose}
                        onClick={onClose}
                        className="w-8 h-8 border border-neutral-300 hover:border-black inline-flex items-center justify-center rounded-sm"
                    >
                        <X size={14} weight="bold" />
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table
                        data-testid={TEST_IDS.comparisonTable}
                        className="w-full border-collapse text-sm"
                    >
                        <thead>
                            <tr className="border-b border-neutral-200 bg-[#F8F8F6]">
                                <th className="text-left px-4 py-3 text-[11px] font-medium uppercase tracking-wider text-neutral-500 sticky left-0 bg-[#F8F8F6]">
                                    Attribute
                                </th>
                                {datasets.map((d) => (
                                    <th
                                        key={d.id}
                                        className="text-left px-4 py-3 min-w-[220px]"
                                    >
                                        <div className="font-serif text-base leading-tight">
                                            {d.organization_name}
                                        </div>
                                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                                            <OrgBadge type={d.organization_type} />
                                            <StatusBadge status={d.status} size="sm" />
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ROWS.map((row) => (
                                <tr
                                    key={row.key}
                                    className="border-b border-neutral-100 hover:bg-neutral-50"
                                >
                                    <td className="px-4 py-3 label-caps sticky left-0 bg-inherit">
                                        {row.label}
                                    </td>
                                    {datasets.map((d) => {
                                        const raw = d[row.key];
                                        const reviewed = d.source_url && d.citation && (d.agree_count || 0) + (d.disagree_count || 0) >= 3;
                                        const display = row.key === "community_rating" && !reviewed ? "Not yet reviewed" : row.fmt
                                            ? row.fmt(raw, d.unit || unit)
                                            : raw == null || raw === ""
                                            ? "—"
                                            : raw;
                                        const isValueRow = row.key === "value";
                                        return (
                                            <td
                                                key={d.id}
                                                className={`px-4 py-3 font-mono text-xs ${
                                                    isValueRow ? "text-black font-semibold text-base" : ""
                                                }`}
                                            >
                                                {display}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            <tr className="border-b border-neutral-100">
                                <td className="px-4 py-3 label-caps sticky left-0 bg-inherit">Source</td>
                                {datasets.map((d) => (
                                    <td key={d.id} className="px-4 py-3 text-xs">
                                        {d.source_url ? (
                                            <a
                                                href={d.source_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[#6B46C1] hover:underline break-all"
                                            >
                                                {d.source_url}
                                            </a>
                                        ) : "—"}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
